// shopifyProducts.js

const PRODUCTS_QUERY = `
  query Products($first: Int!, $after: String) {
	products(first: $first, after: $after) {
	  edges {
		cursor
		node {
		  handle
		  onlineStoreUrl
  
		  # 任意の通常フィールド
		  title
		  description
  		  
		  # 💡 一覧用の代表画像（商品に設定された「メイン画像」）
		  featuredImage {
			url
			altText
			width
			height
		  }
		  collections(first: 5) {
			  edges {
				node {
				  handle
				  title
				  id
				}
			  }
			}

		  priceRange {
			minVariantPrice {
				amount
				currencyCode
			}
			maxVariantPrice {
				amount
				currencyCode
			}
		  }
		  category: metafield(namespace: "custom", key: "item_category") {
			  reference {
				__typename
				... on Metaobject {
				  handle
				  type
				  fields {
					key
					value
				  }
				}
			  }
			}


		  # カスタムメタフィールド
		  priority: metafield(namespace: "custom", key: "priority") { value }
		  name_ja: metafield(namespace: "custom", key: "name_ja") { value }
		  name_en: metafield(namespace: "custom", key: "name_en") { value }
		  description_ja: metafield(namespace: "custom", key: "description_ja") { value }
		  description_en: metafield(namespace: "custom", key: "description_en") { value }
		  product_code: metafield(namespace: "custom", key: "product_code") { value }
		  is_black: metafield(namespace: "custom", key: "is_black") { value }
  		  initials: metafield(namespace: "custom", key: "initials") { value }
		  # カスタム画像 metafield（画像参照型）
		  customImage: metafield(namespace: "custom", key: "top_image") {
			reference {
			  ... on MediaImage {
				image {
				  url
				  altText
				  width
				  height
				}
			  }
			}
		  }
		}
	  }
	  pageInfo { hasNextPage }
	}
  }
`;

export function createProductsClient({ shop, version = '2025-07', token }) {
  if (!shop) throw new Error('`shop` is required');

  const ENDPOINT = `https://${shop}.myshopify.com/api/${version}/graphql.json`;
  const TOKEN = token || document.querySelector('meta[name="shopify-storefront-token"]')?.content?.trim();

  async function fetchProductsPage({ first = 50, after = null } = {}) {
	const res = await fetch(ENDPOINT, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'X-Shopify-Storefront-Access-Token': TOKEN
	  },
	  body: JSON.stringify({ query: PRODUCTS_QUERY, variables: { first, after } }),
	  credentials: 'same-origin'
	});
	if (!res.ok) throw new Error(`[StorefrontAPI] ${res.status} ${res.statusText}`);
	const json = await res.json();
	if (json.errors) throw new Error(`[StorefrontAPI] ${JSON.stringify(json.errors)}`);
	return json?.data?.products ?? { edges: [], pageInfo: { hasNextPage: false } };
  }

  async function fetchAllProducts({ pageSize = 50 } = {}) {
	const all = [];
	let after = null, hasNext = true;
	while (hasNext) {
	  const page = await fetchProductsPage({ first: pageSize, after });
	  const edges = page.edges ?? [];
	  edges.forEach(({ node }) => all.push(formatProduct(node)));
	  hasNext = page.pageInfo?.hasNextPage;
	  after = edges.length ? edges[edges.length - 1].cursor : null;
	}
	return all;
  }

  return { fetchProductsPage, fetchAllProducts };
}

export function formatProduct(node) {
  const bool = (v) => (typeof v === 'string' ? v === 'true' : !!v);
  const toWebp = (url, opts = {}) => {
	  if (!url) return null;
	  const params = new URLSearchParams();
	  if (opts.width) params.set('width', opts.width);
	  params.set('format', 'webp');
	  return `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
	};
  // 一覧用 featuredImage
	const featured = node?.featuredImage
	  ? {
		  url: toWebp(node.featuredImage.url, { width: 1200 }),
		  alt: node.featuredImage.altText || '',
		  width: node.featuredImage.width,
		  height: node.featuredImage.height,
		  srcset: [360, 720, 960, 1200, 1600]
			.map((w) => `${toWebp(node.featuredImage.url, { width: w })} ${w}w`)
			.join(', '),
		}
	  : null;
	  
  // custom.image は1枚だけ → 配列化
  const customImg = node?.customImage?.reference?.image;
  const images = customImg
	? [{
		url: toWebp(customImg.url, { width: 1200 }),
		alt: customImg.altText || '',
		width: customImg.width,
		height: customImg.height,
		srcset: [360, 720, 960, 1200, 1600]
		.map((w) => `${toWebp(customImg.url, { width: w })} ${w}w`)
		.join(', '),
	  }]
	: [];
  // 💰 現在の価格情報（min/max）
	const priceMin = node?.priceRange?.minVariantPrice?.amount ?? null;
	const priceMax = node?.priceRange?.maxVariantPrice?.amount ?? null;
	const currency = node?.priceRange?.minVariantPrice?.currencyCode ?? 'JPY';
  // 💬 コレクション情報
	const collections = (node?.collections?.edges || []).map(({ node }) => ({
	  handle: node.handle,
	  title: node.title,
	  id: node.id,
	}));
  //カテゴリ
	const catRef = node?.category?.reference;
	let category = null;
	if (catRef && catRef.__typename === 'Metaobject') {
		const fields = {};
		for (const f of catRef.fields ?? []) {
		  fields[f.key] = f.value;
		}
		category = {
		  handle: catRef.handle,
		  type: catRef.type,
		  ...fields,
		};
	}
  return {
	handle: node?.handle ?? '',
	title: node.title,
	priority: node?.priority?.value ?? null,
	url: node?.onlineStoreUrl ?? `/products/${node?.handle}`,
	product_code: node?.product_code?.value ?? '',
	title_ja: node?.name_ja?.value ?? '',
	title_en: node?.name_en?.value ?? '',
	is_black: bool(node?.is_black?.value),
	description_ja: node?.description_ja?.value ?? '',
	description_en: node?.description_en?.value ?? '',
	initials: node?.initials?.value ?? '',
	featured_image: featured, // 💡ここに代表画像を追加
	images,
	price_min: priceMin ? parseFloat(priceMin) : null,
	price_max: priceMax ? parseFloat(priceMax) : null,
	currency,
	collections,
	category
  };
}